import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Compass, HandCoins, ShieldCheck, Sparkle,
    Lightning, Users, RocketLaunch, ArrowRight, Envelope,
} from "@phosphor-icons/react";
import Brand from "@/components/Brand";
import { COMPANY } from "@/pages/policies/policyContent";
import useSeo from "@/lib/useSeo";

const PILLARS = [
    {
        icon: HandCoins,
        title: "Candidate-first monetization",
        body:
            "We refuse the industry default: candidates paying with data while agencies " +
            "pocket the value. On NaukriDaddy, candidates pay only for outcomes they " +
            "control — a Verified Badge, Interview Prep, a Featured slot. Agencies pay for " +
            "the platform. Nobody sells you to anyone.",
    },
    {
        icon: ShieldCheck,
        title: "DPDPA-native by design",
        body:
            "Every PII reveal, every impersonation, every Marketplace publish is written " +
            "to a tamper-evident audit trail. You can revoke consent with one click, and " +
            "agencies can't re-publish for 30 days without fresh authorisation from you.",
    },
    {
        icon: Sparkle,
        title: "AI as a coach, not a gatekeeper",
        body:
            "Our LLM tools — CV rewrite, interview prep, matching — help YOU show up " +
            "stronger. We never use AI to reject candidates in bulk or hide profiles " +
            "from recruiters based on opaque scores. Every score is explainable and appealable.",
    },
    {
        icon: Lightning,
        title: "Under-500 ms everywhere it matters",
        body:
            "The Talent Marketplace, Kanban board, and search endpoints all render under " +
            "500 ms P95. If it feels sluggish, it's a bug we treat as production severity — " +
            "not a feature to charge more for on the Enterprise plan.",
    },
];

const NUMBERS = [
    { n: "48h", l: "avg time from apply → recruiter reply" },
    { n: "8", l: "questions per AI-generated interview session" },
    { n: "₹99", l: "for 30 days of Featured placement" },
    { n: "0", l: "candidate data ever sold to third parties" },
];

export default function AboutUs() {
    useSeo({
        title: "About NaukriDaddy · Delhi-based AI Recruitment SaaS | NaukriDaddy",
        description: "Naukridaddy HR Solutions Private Limited — started November 2022 in Delhi as a boutique hiring partner, now India's AI-first recruitment platform where candidates own their data and agencies pay for a curated Marketplace.",
        canonical: "https://naukridaddy.com/about",
    });
    return (
        <div className="min-h-screen bg-white" data-testid="about-page">
            {/* Header */}
            <header className="border-b border-[#E5E5E5]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
                    <Brand />
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-cobalt mb-4 inline-flex items-center gap-2">
                    <Compass size={12} weight="fill" /> Our story · Delhi, India · Since November 2022
                </p>
                <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9] mb-6">
                    We&apos;re building the hiring platform<br />
                    <span className="text-cobalt">candidates actually recommend</span>.
                </h1>
                <p className="text-base sm:text-lg text-[#404040] max-w-2xl leading-relaxed">
                    Every job board treats candidates like inventory. Recruiters get the tools,
                    candidates get the ads. NaukriDaddy flips it: candidates get AI coaches,
                    verified badges, and full data control. Agencies get a Talent Marketplace
                    filled with pre-qualified people who <em>want</em> to be found.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-8">
                    <Link
                        to="/candidate/login"
                        data-testid="about-cta-candidate"
                        className="btn-gradient px-6 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2"
                    >
                        Get shortlisted <ArrowRight size={14} weight="bold" />
                    </Link>
                    <Link
                        to="/login"
                        data-testid="about-cta-recruiter"
                        className="px-6 py-3 text-xs font-black uppercase tracking-wider border-2 border-ink hover:bg-ink hover:text-white inline-flex items-center gap-2"
                    >
                        I&apos;m a recruiter <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* Numbers strip */}
            <section className="bg-ink text-white py-12">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {NUMBERS.map((n, i) => (
                        <div key={i} className="border-l-2 border-splash pl-4">
                            <p className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-splash">{n.n}</p>
                            <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">{n.l}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Story */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-3">Our journey</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-6">
                    From Delhi hiring partner to <span className="text-cobalt">AI-first recruitment SaaS</span>.
                </h2>
                <div className="space-y-5 text-base text-[#404040] leading-relaxed">
                    <p>
                        <b>November 2022 — Delhi.</b> Naukridaddy HR Solutions Private Limited started
                        as a boutique hiring partner, working shoulder-to-shoulder with startups and
                        enterprises to close hard roles. We ran the searches ourselves, spoke to every
                        candidate, and learned exactly where the recruitment stack was broken.
                    </p>
                    <p>
                        <b>2023 — Top hiring search firm.</b> Within twelve months we became one of the
                        most-recommended search firms in the region — not because we had more sourcers,
                        but because we treated candidates like humans, not inventory. That trust
                        compounded: repeat mandates, warmer referrals, better fits.
                    </p>
                    <p>
                        <b>Today — AI-enabled SaaS product.</b> Every recurring pain we saw as a
                        recruiter — buried resumes, opaque scoring, ghosted candidates, illegible
                        pipelines — became a feature. NaukriDaddy is now an AI-first recruitment
                        platform where <b>candidates own their data, control their visibility, and pay
                        only for outcomes they choose</b>, and agencies pay for a Marketplace of
                        pre-qualified, opt-in talent.
                    </p>
                    <p className="italic text-[#525252] border-l-4 border-splash pl-4">
                        &ldquo;We&apos;re a two-sided marketplace, but only one side is our customer:
                        the candidate. Everything else follows from that.&rdquo;
                    </p>
                </div>
            </section>

            {/* Pillars */}
            <section className="bg-[#FAFAFA] py-16 sm:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-3">What we stand for</p>
                    <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-10 max-w-3xl">
                        Four principles we refuse to trade off.
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2">
                        {PILLARS.map((p, i) => (
                            <div key={i} data-testid={`about-pillar-${i}`}
                                 className="bg-white border border-[#E5E5E5] p-6 sm:p-8">
                                <div className="w-10 h-10 bg-ink text-splash flex items-center justify-center mb-4">
                                    <p.icon size={18} weight="fill" />
                                </div>
                                <h3 className="font-heading text-xl font-black text-ink mb-2">{p.title}</h3>
                                <p className="text-sm text-[#404040] leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team placeholder */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
                <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-3">
                    <Users size={12} weight="fill" /> The team
                </div>
                <h2 className="font-heading text-4xl font-black tracking-tighter mb-4">
                    Small team. Sharp opinions.
                </h2>
                <p className="text-sm text-[#525252] max-w-xl mx-auto">
                    Ten people between engineering, ML, and product — half of whom have been on
                    the receiving end of an auto-reject and thought, &lsquo;there has to be a better
                    way.&rsquo; We&apos;re hiring, cautiously.
                </p>
                <a
                    href={`mailto:careers@naukridaddy.com`}
                    data-testid="about-careers-link"
                    className="mt-6 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-cobalt hover:text-ink"
                >
                    careers@naukridaddy.com <ArrowRight size={12} weight="bold" />
                </a>
            </section>

            {/* Contact */}
            <section className="bg-ink text-white py-16 sm:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <RocketLaunch size={36} weight="fill" className="text-splash mx-auto mb-4" />
                    <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
                        Ready when you are.
                    </h2>
                    <p className="text-base text-white/70 max-w-xl mx-auto mb-8">
                        Whether you&apos;re looking for your next role or your next 10 hires,
                        we&apos;d rather show than tell.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <Link to="/candidate/login" className="btn-gradient px-6 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2">
                            Start free <ArrowRight size={14} weight="bold" />
                        </Link>
                        <a
                            href={`mailto:${COMPANY.support_email}`}
                            className="px-6 py-3 text-xs font-black uppercase tracking-wider border-2 border-white/30 hover:border-white hover:bg-white/10 inline-flex items-center gap-2"
                        >
                            <Envelope size={14} weight="fill" /> {COMPANY.support_email}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
